import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { connectEzyVet, disconnectEzyVet, getEzyVetStatus, syncEzyVet } from "@/api/integrations";
import { ConnectionDiagnostics } from "@/components/ezyvet/ConnectionDiagnostics";
import { CredentialsForm, type CredentialsValues } from "@/components/ezyvet/CredentialsForm";
import { EzyVetSettingsTabs } from "@/components/ezyvet/EzyVetSettingsTabs";
import { RateLimitWidget } from "@/components/ezyvet/RateLimitWidget";
import { WebhookEndpointCard } from "@/components/ezyvet/WebhookEndpointCard";
import { QueryError } from "@/components/ui/QueryError";
import { StatusBadge } from "@/components/ui/StatusBadge";
import { useTenant } from "@/hooks/useTenant";
import { useToasts } from "@/hooks/useToast";
import type { ApiError, EzyVetStatus } from "@/types";

function formatTime(value: string | null, empty: string): string {
  return value ? new Date(value).toLocaleString() : empty;
}

/** /settings/integrations/ezyvet: credentials, diagnostics, webhook endpoint and quota. */
export function EzyVetSettingsPage() {
  const qc = useQueryClient();
  const push = useToasts((s) => s.push);
  const { isAdmin } = useTenant();
  const [editing, setEditing] = useState(false);

  const status = useQuery<EzyVetStatus, ApiError>({
    queryKey: ["integrations", "ezyvet"],
    queryFn: getEzyVetStatus,
  });

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ["integrations", "ezyvet"] });
    qc.invalidateQueries({ queryKey: ["ezyvet", "telemetry"] });
    qc.invalidateQueries({ queryKey: ["me"] });
  };
  const onError = (err: ApiError) => {
    push(err.message ?? "Request failed", "error");
    refresh();
  };

  const connect = useMutation<EzyVetStatus, ApiError, CredentialsValues>({
    mutationFn: (v) =>
      connectEzyVet({
        ...v,
        partner_id: v.partner_id || undefined,
        site_uid: v.site_uid || undefined,
        scope: v.scope || undefined,
      }),
    onSuccess: () => {
      push("ezyVet connected. The first sync looks back 30 days.", "success");
      setEditing(false);
      refresh();
    },
    onError,
  });
  const sync = useMutation<void, ApiError>({
    mutationFn: syncEzyVet,
    onSuccess: () => push("Sync queued. New consults appear under Appointments.", "success"),
    onError,
  });
  const disconnect = useMutation<void, ApiError>({
    mutationFn: disconnectEzyVet,
    onSuccess: () => {
      push("ezyVet disconnected; stored credentials were deleted.", "success");
      refresh();
    },
    onError,
  });

  const data = status.data;
  const busy = connect.isPending || sync.isPending || disconnect.isPending;
  const showForm = isAdmin && (!data?.connected || editing);

  return (
    <div>
      <div className="page-header">
        <h1>ezyVet integration</h1>
        <p className="page-sub">Consults and invoices flow in from ezyVet and are audited automatically, with no uploads.</p>
        <EzyVetSettingsTabs />
      </div>

      {status.isLoading && (
        <div className="card loading">
          <div className="spinner" />
          <div>Loading…</div>
        </div>
      )}
      {status.error && <QueryError error={status.error} what="the ezyVet connection" onRetry={() => status.refetch()} />}

      {data && (
        <div className="settings-grid">
          <div className="stack">
            {data.connected && (
              <section className="card" aria-labelledby="conn-title">
                <div className="row" style={{ justifyContent: "space-between" }}>
                  <h2 id="conn-title" style={{ margin: 0 }}>
                    Connection
                  </h2>
                  <StatusBadge tone={data.status === "active" ? "synced" : "unbilled"}>
                    {data.status === "active" ? "Connected" : "Needs attention"}
                  </StatusBadge>
                </div>
                {data.last_error && (
                  <div className="callout error" role="alert" style={{ marginTop: 12 }}>
                    {data.last_error}
                  </div>
                )}
                <dl className="kv">
                  <dt>Site</dt>
                  <dd className="mono">{data.site}</dd>
                  <dt>Environment</dt>
                  <dd>{data.environment}</dd>
                  <dt>Client ID</dt>
                  <dd className="mono">••••{data.client_id_hint}</dd>
                  <dt>Token expires</dt>
                  <dd>{formatTime(data.token_expires_at, "No token")}</dd>
                  <dt>Last sync</dt>
                  <dd>{formatTime(data.last_synced_at, "Never")}</dd>
                </dl>
                {isAdmin && (
                  <>
                    <div className="row" style={{ flexWrap: "wrap" }}>
                      <button className="btn btn-sm" disabled={busy || data.status !== "active"} onClick={() => sync.mutate()}>
                        Sync now
                      </button>
                      <button className="btn btn-sm" disabled={busy} onClick={() => setEditing((e) => !e)} aria-expanded={editing}>
                        {editing ? "Cancel" : "Update credentials"}
                      </button>
                      <button
                        className="btn btn-sm btn-danger"
                        disabled={busy}
                        onClick={() => {
                          if (window.confirm("Disconnect ezyVet? Stored credentials will be deleted; past audits stay.")) {
                            disconnect.mutate();
                          }
                        }}
                      >
                        Disconnect
                      </button>
                    </div>
                    <ConnectionDiagnostics disabled={busy} />
                  </>
                )}
              </section>
            )}

            {data && !data.connected && !isAdmin && (
              <div className="card muted">ezyVet isn&apos;t connected. Ask a clinic admin to connect it here.</div>
            )}

            {showForm && (
              <CredentialsForm
                busy={connect.isPending}
                reconnecting={data.connected}
                onSubmit={(v) => connect.mutate(v)}
                onCancel={data.connected ? () => setEditing(false) : undefined}
              />
            )}

            {data.connected && data.status !== "revoked" && <WebhookEndpointCard status={data} isAdmin={isAdmin} />}
          </div>

          {data.connected && (
            <aside className="stack">
              <RateLimitWidget isAdmin={isAdmin} />
            </aside>
          )}
        </div>
      )}
    </div>
  );
}
